import { Core } from "@faire/design-tokens";
import trackClick from "@faire/web-api/api/click-track/post";
import { IClickInfo } from "@faire/web-api/indigofair/data/IClickInfo";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { TrackedLink } from "@faire/web/ui/TrackedLink";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import React from "react";
import styled from "styled-components";

import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer/lib/verticalAspectRatio";

interface IProps {
  token: string;
  link: string;
  text: string;
  image?: IImage;
  tileOnClick: (token?: string) => void;
  onClickEventName?: string;
  useLazyImageLoading?: boolean;
}

const ZOOM_IN_SCALE = 1.1;
const IMAGE_HEIGHT = 416;
const OVERLAY_Z_INDEX = 2;

export const PosterTile: React.FC<IProps> = ({
  token,
  link,
  image,
  text,
  tileOnClick,
  onClickEventName,
  useLazyImageLoading,
}) => {
  const isSSR = useIsSSR();
  const imageLink = image
    ? optimizeImageUrl(image, isSSR, {
        resizeMethod: "fill",
        width: VERTICAL_ASPECT_RATIO * IMAGE_HEIGHT,
        height: IMAGE_HEIGHT,
      })
    : EAEAEA_PIXEL;

  return (
    <StyledLink
      token={token}
      to={link}
      onClick={() => {
        trackClick(
          IClickInfo.build({
            token,
            type: IClickInfo.Type.PRODUCT,
            container_name: onClickEventName,
          })
        );
        tileOnClick(token);
      }}
      onDragStart={onDragStart}
    >
      <StyledAspectRatioPicture
        $width="100%"
        $ratio={VERTICAL_ASPECT_RATIO}
        $hideBorder
      >
        <Overlay />
        <StyledImage
          src={imageLink}
          alt={text}
          loading={useLazyImageLoading ? "lazy" : undefined}
        />
        {text !== undefined ? (
          <StyledTypography>
            <AnimatedUnderlineText>{text}</AnimatedUnderlineText>
          </StyledTypography>
        ) : null}
      </StyledAspectRatioPicture>
    </StyledLink>
  );
};

const onDragStart = (e: React.MouseEvent) => e.preventDefault();

const StyledImage = styled.img`
  transition: transform 1200ms cubic-bezier(0.17, 0.67, 0.24, 1);
  transform-origin: left;
`;

const AnimatedUnderlineText = styled.span`
  background-image: linear-gradient(
    ${Core.surface.inverse},
    ${Core.surface.inverse}
  );
  background-size: 0 2px;
  background-position: 0 100%;
  background-repeat: no-repeat;
  transition: background-size 1200ms cubic-bezier(0.17, 0.67, 0.24, 1);
  padding-bottom: 5px;
`;

const Overlay = styled(TransparencyOverlay).attrs({
  color: "linear-gradient(to left bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 1))",
})`
  transition: opacity 300ms ease-in-out;
  opacity: 20%;
  z-index: ${OVERLAY_Z_INDEX};
`;

const StyledLink = styled(TrackedLink)`
  display: flex;
  text-decoration: none;
  flex-direction: column;
  align-items: flex-start;

  ${responsiveMediaQueries.canHover} {
    &:hover {
      text-decoration: none;
      ${StyledImage} {
        transform: scale(${ZOOM_IN_SCALE});
      }
      ${AnimatedUnderlineText} {
        background-size: 100% 2px;
      }
      ${Overlay} {
        opacity: 40%;
      }
    }
  }
`;

const StyledAspectRatioPicture = styled(AspectRatioPicture)`
  overflow: hidden;
`;

const StyledTypography = styled(Typography).attrs({
  variant: {
    mobileAndAbove: "subheadingSansMedium",
    tabletAndAbove: "sectionHeaderSansMedium",
  },
  color: Core.text.inverse,
})`
  position: absolute;
  left: 16px;
  bottom: 16px;
  z-index: ${OVERLAY_Z_INDEX + 1};
`;
